import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

/**
 * MFA バックアップコードの出力ヘルパー（#2984・PRD §5-3）。
 * クリップボードコピーと PDF ダウンロードの 2 形式を提供する。
 * PDF は標準フォント（Courier）で描画するため ASCII のみで構成する。
 */

const PDF_PAGE_WIDTH = 595; // A4 (pt)
const PDF_PAGE_HEIGHT = 842;
const PDF_MARGIN = 64;
const PDF_TITLE_SIZE = 18;
const PDF_BODY_SIZE = 13;
const PDF_LINE_HEIGHT = 26;
const PDF_MIME_TYPE = 'application/pdf';
const PDF_FILE_NAME = 'gen-drive-backup-codes.pdf';

/** コード一覧をクリップボードへコピーする */
export const copyBackupCodesToClipboard = async (codes: string[]): Promise<void> => {
  await navigator.clipboard.writeText(codes.join('\n'));
};

/** コード一覧を PDF としてダウンロードする */
export const downloadBackupCodesPdf = async (codes: string[], email: string): Promise<void> => {
  const doc = await PDFDocument.create();
  const page = doc.addPage([PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT]);
  const titleFont = await doc.embedFont(StandardFonts.CourierBold);
  const bodyFont = await doc.embedFont(StandardFonts.Courier);

  let y = PDF_PAGE_HEIGHT - PDF_MARGIN;
  page.drawText('Gen Drive - Backup Codes', {
    x: PDF_MARGIN,
    y,
    size: PDF_TITLE_SIZE,
    font: titleFont,
    color: rgb(0, 0, 0),
  });
  y -= PDF_LINE_HEIGHT * 1.5;
  // 日本語は標準フォントで描画できないため英語表記とする
  page.drawText(`Account: ${email}`, { x: PDF_MARGIN, y, size: PDF_BODY_SIZE, font: bodyFont });
  y -= PDF_LINE_HEIGHT;
  page.drawText('Keep these codes in a safe place.', {
    x: PDF_MARGIN,
    y,
    size: PDF_BODY_SIZE,
    font: bodyFont,
  });
  y -= PDF_LINE_HEIGHT;
  page.drawText('Each code can be used only once.', {
    x: PDF_MARGIN,
    y,
    size: PDF_BODY_SIZE,
    font: bodyFont,
  });
  y -= PDF_LINE_HEIGHT * 1.5;
  codes.forEach((code, index) => {
    page.drawText(`${String(index + 1).padStart(2, ' ')}. ${code}`, {
      x: PDF_MARGIN,
      y,
      size: PDF_BODY_SIZE,
      font: bodyFont,
    });
    y -= PDF_LINE_HEIGHT;
  });

  // data URI のままクリックすると Chrome が download 属性のファイル名を無視し、
  // UUID 名の .txt で保存されてしまうため、他のダウンロード処理と同じく
  // Blob + オブジェクト URL を使い、アンカーを DOM に追加してからクリックする（#3215）
  const pdfBytes = await doc.save();
  // Uint8Array をそのまま渡すと ArrayBuffer の型が合わないため、専用のバッファへ写してから Blob 化する
  const blob = new Blob([new Uint8Array(pdfBytes)], { type: PDF_MIME_TYPE });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = PDF_FILE_NAME;
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    // 例外が起きてもアンカーとオブジェクト URL を残さない
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  }
};
